import { useState, useEffect, useRef } from 'react'
import { getFlowConfig } from '../utils/cardFlowConfig'
import { getOwnerCardDisplayName } from '../utils/ownerCardDisplay'
import BurnedCardPlaceholder from './BurnedCardPlaceholder'
import { getCardTypeKey, getCardTypeLabel } from '../utils/cardTypes'
import { PolygonMark } from './m1nt/card/SealEmblem'

function typeCaption(rawType) {
  return rawType ? getCardTypeLabel(getCardTypeKey(rawType)) : '—'
}

/** Token ids are shown as "#123"; very long ids are shortened in the middle. */
function formatTokenId(value) {
  if (value == null) return null
  const text = String(value).trim()
  if (!text) return null
  return text.length > 7 ? `#${text.slice(0, 3)}…${text.slice(-3)}` : `#${text}`
}

function getImageSrc(card, imageUrl) {
  return imageUrl || card?.frontImageUrl || card?.frontImage || '/placeholder-card.png'
}

function formatCountdownMs(ms) {
  if (ms <= 0) return '0:00:00'
  const s = Math.floor(ms / 1000)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

const FACE_CLASS =
  'absolute inset-0 overflow-hidden rounded-t-[12px] [backface-visibility:hidden] [-webkit-backface-visibility:hidden]'

function handleImageError(event) {
  event.target.src = '/placeholder-card.png'
  event.target.onerror = null
}

/** Card photo framed directly in the tile area, with no synthetic background. */
function PhotoFill({ src, alt = '', ...imgProps }) {
  return (
    <img
      src={src}
      alt={alt}
      draggable={false}
      loading="lazy"
      className="block h-full w-full select-none object-cover object-top"
      onError={handleImageError}
      {...imgProps}
    />
  )
}

const DRAG_THRESHOLD_PX = 6
/** Dragging about 28% of the tile's width turns the card a quarter turn (the tipping point). */
const DRAG_SENSITIVITY = 1.8

/**
 * Square art frame of a card tile (same proportions as an NFT marketplace tile): the photo fills it edge to edge, cropped when needed, with no backdrop. The image fills the frame and can be cropped so the visual
 * result matches a marketplace card. Press and drag sideways to turn the card over and see its
 * back; a plain click (no drag) is left alone so it opens the card.
 */
function CardTileMedia({
  displayName,
  isBurned,
  burnedName,
  loading,
  frontSrc,
  backSrc,
  compact = false,
}) {
  const canFlip = !isBurned && !loading && Boolean(backSrc)

  // `rest` is the angle the card settles at (a multiple of 180); `dragDelta` is the live drag.
  const [rest, setRest] = useState(0)
  const [dragDelta, setDragDelta] = useState(0)
  const [dragging, setDragging] = useState(false)
  const rootRef = useRef(null)
  const dragRef = useRef({ active: false, startX: 0, moved: false, pointerId: null, delta: 0 })

  const liveAngle = Math.min(rest + 180, Math.max(rest - 180, rest + dragDelta))

  const handlePointerDown = (event) => {
    if (!canFlip) return
    if (event.pointerType === 'mouse' && event.button !== 0) return
    dragRef.current = {
      active: true,
      startX: event.clientX,
      moved: false,
      pointerId: event.pointerId,
      delta: 0,
    }
  }

  const handlePointerMove = (event) => {
    const drag = dragRef.current
    if (!drag.active) return
    const dx = event.clientX - drag.startX
    if (!drag.moved) {
      if (Math.abs(dx) < DRAG_THRESHOLD_PX) return
      drag.moved = true
      setDragging(true)
      event.currentTarget.setPointerCapture?.(drag.pointerId)
    }
    const width = rootRef.current?.offsetWidth || 200
    drag.delta = (dx / width) * 180 * DRAG_SENSITIVITY
    setDragDelta(drag.delta)
  }

  const finishDrag = () => {
    const drag = dragRef.current
    if (!drag.active) return
    drag.active = false
    if (!drag.moved) return
    // Read from the ref: the last pointer move may not have re-rendered yet.
    setRest(
      (angle) =>
        Math.round(Math.min(angle + 180, Math.max(angle - 180, angle + drag.delta)) / 180) * 180
    )
    setDragDelta(0)
    setDragging(false)
  }

  // A drag ends in a click event; swallow it so the tile does not open the card.
  const handleClickCapture = (event) => {
    if (dragRef.current.moved) {
      event.stopPropagation()
      dragRef.current.moved = false
    }
  }

  const handleFlipButton = (event) => {
    event.stopPropagation()
    setRest((angle) => (Math.round(angle / 180) % 2 === 0 ? angle + 180 : angle - 180))
  }

  let media
  if (isBurned) {
    media = (
      <div className="relative h-full w-full overflow-hidden rounded-t-[12px]">
        <BurnedCardPlaceholder cardName={burnedName} />
      </div>
    )
  } else if (loading) {
    media = (
      <div className="flex h-full w-full items-center justify-center">
        <span className="h-6 w-6 rounded-full border border-m1-line border-t-m1-violet motion-safe:animate-spin" />
      </div>
    )
  } else if (canFlip) {
    media = (
      <div
        style={{ transform: `rotateY(${liveAngle}deg)` }}
        className={`relative h-full w-full [transform-style:preserve-3d] ${
          dragging
            ? ''
            : 'transition-transform duration-[750ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:duration-[1ms]'
        }`}
      >
        <div className={FACE_CLASS}>
          <PhotoFill src={frontSrc} alt={displayName} />
        </div>
        <div className={`${FACE_CLASS} [transform:rotateY(180deg)]`}>
          {/* Rendered up front (at low priority, after the fronts) so the flip never waits on the network. */}
          <PhotoFill src={backSrc} decoding="async" fetchPriority="low" />
        </div>
      </div>
    )
  } else {
    media = (
      <div className="relative h-full w-full overflow-hidden rounded-t-[12px]">
        <PhotoFill src={frontSrc} alt={displayName} />
      </div>
    )
  }

  return (
    <div
      ref={rootRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onClickCapture={handleClickCapture}
      style={dragging ? { cursor: 'grabbing' } : undefined}
      className={`relative aspect-square w-full overflow-hidden rounded-t-[12px] bg-mk-raised [perspective:900px] ${
        canFlip ? 'select-none [touch-action:pan-y]' : ''
      } ${isBurned ? 'opacity-90' : ''}`}
    >
      {media}
      {canFlip && !compact && (
        <button
          type="button"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={handleFlipButton}
          aria-label="Turn the card over"
          title="Turn over (or drag the card)"
          className={`absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/80 text-white transition-opacity duration-150 hover:text-m1-violet focus:outline-none focus-visible:opacity-100 focus-visible:ring-1 focus-visible:ring-m1-violet [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100`}
        >
          <i className="ri-refresh-line text-base" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

/** Diamond glyph used by the marketplace token chip (24-unit grid). */
function DiamondIcon({ className }) {
  return (
    <svg viewBox="0 -960 960 960" className={className} fill="currentColor" aria-hidden="true">
      <path d="M480-200 200-480l280-280 280 280-280 280Z" />
    </svg>
  )
}

/**
 * Info block under the art, laid out like a marketplace tile: row 1 holds the name and the
 * token id chip, row 2 holds the network and, in small print below it, the flow status.
 * Notices (custody, approvals) get their own strip below.
 */
function CardCaption({
  displayName,
  tokenId,
  statusLabel,
  pulse,
  attention,
  unread,
  notices,
  compact,
}) {
  const tokenLabel = formatTokenId(tokenId)
  return (
    <div className="relative flex min-h-24 flex-1 flex-col">
      <div
        className={`flex w-full items-center justify-between gap-3 px-3 ${compact ? 'h-9' : 'h-12'}`}
      >
        <span
          title={displayName}
          className={`min-w-0 flex-1 truncate font-medium leading-normal text-white ${
            compact ? 'text-xs' : 'text-sm'
          }`}
        >
          {displayName}
        </span>
        {unread && (
          <span
            className="h-2 w-2 shrink-0 rounded-full bg-m1-violet"
            title="Updates"
            aria-label="Updates"
          />
        )}
        {tokenLabel && !compact && (
          <span
            title={`Token ID ${tokenId}`}
            className="flex h-5 shrink-0 items-center gap-0 rounded bg-mk-raised pl-[0.5px] pr-1 text-mk-muted"
          >
            <DiamondIcon className="h-[18px] w-[18px] shrink-0" />
            <span className="font-m1-mono text-xs uppercase leading-tight">{tokenLabel}</span>
          </span>
        )}
      </div>
      <div className={`flex w-full flex-col px-3 ${compact ? 'pb-2.5' : 'min-h-12 pb-3.5'}`}>
        {!compact && (
          <span className="flex min-w-0 items-center gap-1.5 text-sm leading-normal">
            {tokenLabel ? (
              <>
                <span className="truncate font-m1-mono text-white">Polygon</span>
                <PolygonMark className="h-3.5 w-3.5 shrink-0 text-[#8247E5]" />
              </>
            ) : (
              <span className="truncate font-m1-mono text-mk-muted">Not minted</span>
            )}
          </span>
        )}
        <p
          title={statusLabel}
          className={`flex min-w-0 items-center gap-1.5 truncate leading-normal ${
            compact ? 'text-[10px]' : '-mt-1 text-[10px] xl:text-[12px]'
          } ${attention ? 'text-m1-amber' : 'text-mk-muted'}`}
        >
          <span
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${
              attention ? 'bg-m1-amber' : 'bg-m1-violet'
            } ${pulse ? 'card-journey-dot-pulse' : ''}`}
            aria-hidden="true"
          />
          <span className="truncate">{statusLabel}</span>
        </p>
      </div>
      {notices.length > 0 && (
        <ul className="mt-auto space-y-1 border-t border-mk-line px-3 py-2.5">
          {notices.map((notice) => (
            <li
              key={notice}
              className="flex items-start gap-1.5 font-m1-mono text-[10px] uppercase leading-snug tracking-[0.08em] text-m1-amber"
            >
              <i
                className="ri-error-warning-line shrink-0 text-[13px] leading-none"
                aria-hidden="true"
              />
              {notice}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * A collection tile in the marketplace style: rounded card with a hairline border, square media,
 * a two-row info block, a lift on hover and an action bar that slides up from the bottom.
 */
function CardTile({ media, caption, compact }) {
  return (
    <div
      className={`relative z-[1] flex h-full flex-1 flex-col overflow-hidden rounded-[12px] bg-mk-primary shadow-xs before:pointer-events-none before:absolute before:inset-0 before:z-20 before:rounded-[inherit] before:border before:border-white/[0.08]`}
    >
      <div
        className={`flex h-full w-full flex-col overflow-hidden rounded-[inherit] transition-transform duration-200 ease-out [@media(hover:hover)]:group-hover:-translate-y-0.5 [@media(hover:hover)]:group-hover:scale-[1.01] active:scale-[1.005]`}
      >
        {media}
        {caption}
      </div>
      {!compact && (
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-x-0 -bottom-px z-10 flex h-12 translate-y-full items-center justify-between bg-m1-violet px-3 text-sm font-medium text-m1-ground transition-transform duration-150 ease-out group-focus-visible:translate-y-0 [@media(hover:hover)]:group-hover:translate-y-0`}
        >
          View card
          <i className="ri-arrow-right-line text-lg" aria-hidden="true" />
        </div>
      )}
    </div>
  )
}

/**
 * Tile grid: columns fill the available width (min tile width per breakpoint), so the filter
 * rail opening or closing just reflows the cards. Phones always get two columns.
 */
function listClasses(view) {
  if (view === 'list') return 'flex flex-col border-b border-m1-line'
  if (view === 'compact') {
    return 'grid grid-cols-3 gap-3 sm:grid-cols-[repeat(auto-fill,minmax(112px,1fr))]'
  }
  return 'grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fill,minmax(172px,1fr))] lg:grid-cols-[repeat(auto-fill,minmax(178px,1fr))] xl:grid-cols-[repeat(auto-fill,minmax(186px,1fr))] 2xl:grid-cols-[repeat(auto-fill,minmax(218px,1fr))]'
}

function formatRowDate(value) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** One card as a table-like row: thumbnail, name, type, status and date. */
function CardListRow({
  displayName,
  cardType,
  statusLabel,
  attention,
  pulse,
  isBurned,
  frontSrc,
  loading,
  unread,
  notices,
  date,
}) {
  return (
    <div className="group flex items-center gap-4 py-3 pr-1 transition-colors hover:bg-white/[0.03] sm:gap-6 sm:pr-3">
      <div
        className={`relative h-[84px] w-[60px] shrink-0 overflow-hidden border bg-m1-surface transition-colors sm:ml-2 ${
          attention ? 'border-m1-amber/70' : 'border-m1-line group-hover:border-m1-violet'
        } ${isBurned ? 'opacity-70' : ''}`}
      >
        {isBurned ? (
          <span className="flex h-full w-full items-center justify-center text-m1-muted">
            <i className="ri-fire-line text-xl" aria-hidden="true" />
          </span>
        ) : loading ? (
          <span className="flex h-full w-full items-center justify-center">
            <span className="h-4 w-4 rounded-full border border-m1-line border-t-m1-violet motion-safe:animate-spin" />
          </span>
        ) : (
          <img
            src={frontSrc}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            onError={handleImageError}
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate text-[16px] font-normal leading-tight text-m1-ink transition-colors group-hover:text-m1-violet">
            {displayName}
          </h3>
          {unread && (
            <span
              className="h-2 w-2 shrink-0 rounded-full bg-m1-violet"
              title="Updates"
              aria-label="Updates"
            />
          )}
        </div>
        <p className="mt-1 truncate text-[14px] font-light text-m1-muted">
          {typeCaption(cardType)}
        </p>
        <p
          className={`mt-2 inline-flex items-center gap-2 font-m1-mono text-[11px] uppercase tracking-[0.12em] sm:hidden ${
            attention ? 'text-m1-amber' : 'text-m1-violet'
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${attention ? 'bg-m1-amber' : 'bg-m1-violet'}`}
            aria-hidden="true"
          />
          {statusLabel}
        </p>
      </div>
      <p
        className={`hidden w-[210px] shrink-0 items-center gap-2 font-m1-mono text-[11px] uppercase tracking-[0.12em] sm:inline-flex ${
          attention ? 'text-m1-amber' : 'text-m1-violet'
        }`}
      >
        <span
          className={`h-1.5 w-1.5 shrink-0 rounded-full ${attention ? 'bg-m1-amber' : 'bg-m1-violet'} ${
            pulse ? 'card-journey-dot-pulse' : ''
          }`}
          aria-hidden="true"
        />
        <span className="truncate">{statusLabel}</span>
      </p>
      <div className="hidden w-[120px] shrink-0 text-right md:block">
        {notices[0] ? (
          <span className="font-m1-mono text-[10px] uppercase leading-snug tracking-[0.1em] text-m1-amber">
            {notices[0]}
          </span>
        ) : (
          <span className="text-[13px] font-light text-m1-muted">{date || ''}</span>
        )}
      </div>
      <i
        className="ri-arrow-right-s-line shrink-0 text-xl text-m1-muted transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-m1-violet"
        aria-hidden="true"
      />
    </div>
  )
}

export default function CardGrid({
  items,
  view = 'grid',
  cardImages,
  loadingImages,
  onCardClick,
  hasUnreadForOrder,
  onCustodyReview,
  onRequestLabCustodyClaim,
  ownerHasLinkedWallet = false,
}) {
  const hasActiveCustody = items?.some((i) => i.custodyRequest && !i.custodyRequest.isExpired)
  const [nowTick, setNowTick] = useState(() => Date.now())
  const [claimWalletByCardId, setClaimWalletByCardId] = useState({})
  const [claimSubmittingByCardId, setClaimSubmittingByCardId] = useState({})
  const [claimFeedbackByCardId, setClaimFeedbackByCardId] = useState({})

  useEffect(() => {
    if (!hasActiveCustody) return undefined
    const t = setInterval(() => setNowTick(Date.now()), 1000)
    return () => clearInterval(t)
  }, [hasActiveCustody])

  return (
    <ul className={listClasses(view)}>
      {items.map((item) => {
        const { card, order, custodyRequest } = item
        const isBurned = card?.status === 'BURNED'
        const burnAwaitingSecond = Boolean(item.burnPendingAwaitingSecondApproval)
        const cfg = isBurned
          ? { label: 'Burned', color: 'orange', pulse: false }
          : getFlowConfig(order?.status || 'PENDING_QUOTE')
        const img = getImageSrc(card, cardImages[card.id]?.frontImageUrl)
        const unread = hasUnreadForOrder?.(order?.id, card.id)
        const custodyActive = Boolean(custodyRequest && !custodyRequest.isExpired)
        const labCustodyClaimPending = Boolean(
          item.labCustodyClaim && item.labCustodyClaim.status === 'PENDING_LAB'
        )
        const custodyLeftMs = custodyRequest?.dueAt
          ? new Date(custodyRequest.dueAt).getTime() - nowTick
          : 0
        const displayName = getOwnerCardDisplayName(card)
        const attention = isBurned || burnAwaitingSecond
        const notices = []
        if (custodyActive) notices.push('Custody pending')
        if (burnAwaitingSecond && !isBurned) notices.push('Awaiting 2nd approval')
        const attrs = card?.attributes && typeof card.attributes === 'object' ? card.attributes : {}
        const isLabCustodyMint = attrs.labCustodyMint === true
        const canRequestLabCustodyOwnership =
          !isBurned &&
          !ownerHasLinkedWallet &&
          isLabCustodyMint &&
          !labCustodyClaimPending &&
          typeof onRequestLabCustodyClaim === 'function'
        const claimWalletInput = claimWalletByCardId[card.id] || ''
        const claimSubmitting = Boolean(claimSubmittingByCardId[card.id])
        const claimFeedback = claimFeedbackByCardId[card.id]

        const extras = (
          <>
            {custodyActive && onCustodyReview && (
              <div className="mt-3 space-y-2 border-t border-m1-amber/40 pt-3">
                <p className="font-m1-mono text-[12px] text-m1-amber">
                  Time left: {formatCountdownMs(Math.max(0, custodyLeftMs))}
                </p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onCustodyReview(item)
                  }}
                  className="min-h-[44px] w-full border border-m1-amber/60 px-3 text-[13px] tracking-[0.04em] text-m1-amber transition-colors hover:border-m1-ink hover:text-m1-ink"
                >
                  Approve custody transfer
                </button>
              </div>
            )}

            {(canRequestLabCustodyOwnership || labCustodyClaimPending) && (
              <div className="mt-3 space-y-3 border-t border-m1-line pt-3">
                <p className="text-[14px] font-light leading-relaxed text-m1-ink-soft">
                  Mint Lab has custody of this card.
                </p>
                {labCustodyClaimPending ? (
                  <p className="border border-m1-violet/40 px-3 py-2.5 text-center text-[13px] text-m1-violet">
                    Ownership request pending
                  </p>
                ) : (
                  <>
                    <input
                      type="text"
                      value={claimWalletInput}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const next = e.target.value
                        setClaimWalletByCardId((prev) => ({ ...prev, [card.id]: next }))
                        setClaimFeedbackByCardId((prev) => ({ ...prev, [card.id]: null }))
                      }}
                      placeholder="Enter your wallet address"
                      className="min-h-[44px] w-full border border-m1-line bg-transparent px-3 text-[14px] text-m1-ink placeholder:text-m1-muted focus:border-m1-violet focus:outline-none"
                    />
                    <button
                      type="button"
                      disabled={claimSubmitting || !claimWalletInput.trim()}
                      onClick={async (e) => {
                        e.stopPropagation()
                        const walletInput = claimWalletInput.trim()
                        setClaimSubmittingByCardId((prev) => ({ ...prev, [card.id]: true }))
                        try {
                          const result = await onRequestLabCustodyClaim(card.id, walletInput)
                          if (result?.ok) {
                            setClaimFeedbackByCardId((prev) => ({
                              ...prev,
                              [card.id]: { type: 'success', text: 'Request sent successfully.' },
                            }))
                          } else {
                            setClaimFeedbackByCardId((prev) => ({
                              ...prev,
                              [card.id]: {
                                type: 'error',
                                text: result?.error || 'Could not send request.',
                              },
                            }))
                          }
                        } finally {
                          setClaimSubmittingByCardId((prev) => ({ ...prev, [card.id]: false }))
                        }
                      }}
                      className="min-h-[44px] w-full border border-white/30 px-3 text-[13px] tracking-[0.04em] text-m1-ink transition-colors hover:border-m1-violet hover:text-m1-violet disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/30 disabled:hover:text-m1-ink"
                    >
                      {claimSubmitting ? 'Submitting…' : 'Request card ownership'}
                    </button>
                    {claimFeedback?.text && (
                      <p
                        className={`text-[13px] leading-relaxed ${
                          claimFeedback.type === 'success' ? 'text-m1-ice' : 'text-m1-amber'
                        }`}
                      >
                        {claimFeedback.text}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )

        return (
          <li
            key={card.id}
            className={`flex min-w-0 flex-col ${view === 'list' ? 'border-t border-m1-line' : ''}`}
          >
            <div
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onCardClick(item)
                }
              }}
              onClick={() => onCardClick(item)}
              className="group flex flex-1 cursor-pointer flex-col text-left focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-m1-violet"
            >
              {view === 'list' ? (
                <CardListRow
                  displayName={displayName}
                  cardType={card.cardType}
                  statusLabel={cfg.label}
                  attention={attention}
                  pulse={cfg.pulse}
                  isBurned={isBurned}
                  frontSrc={img}
                  loading={loadingImages && !cardImages[card.id]?.frontImageUrl}
                  unread={Boolean(unread)}
                  notices={notices}
                  date={formatRowDate(order?.createdAt || card?.createdAt)}
                />
              ) : (
                <CardTile
                  compact={view === 'compact'}
                  media={
                    <CardTileMedia
                      displayName={displayName}
                      isBurned={isBurned}
                      burnedName={card?.name}
                      loading={loadingImages && !cardImages[card.id]?.frontImageUrl}
                      frontSrc={img}
                      backSrc={cardImages[card.id]?.backImageUrl || null}
                      compact={view === 'compact'}
                    />
                  }
                  caption={
                    <CardCaption
                      displayName={displayName}
                      tokenId={card.tokenId ?? order?.tokenId}
                      statusLabel={cfg.label}
                      pulse={cfg.pulse}
                      attention={attention}
                      unread={Boolean(unread)}
                      notices={notices}
                      compact={view === 'compact'}
                    />
                  }
                />
              )}
            </div>

            {extras}
          </li>
        )
      })}
    </ul>
  )
}
